// The client side of the API's measurement-unit contract
// (`modules/Nutrition/Application/Services/MeasurementUnit/`): pick a unit,
// send the quantity and its label, let the server derive the grams. Mirrors
// the mobile app's `measurement-unit.utils.ts` so both clients behave alike.

import {
  ComponentAmount,
  ComponentAmountPayload,
  MeasurementUnit,
  MeasurementUnitType,
} from "../types/recipe";
import { formatNumber } from "./formatters";

// API: MeasurementUnitResolver::GRAM_PRECISION.
const GRAM_PRECISION = 3;

// A quantity is stored with two decimals (`decimal:0,2` on the API).
export const QUANTITY_MAX_DECIMALS = 2;
export const QUANTITY_MIN = 0.01;
export const QUANTITY_MAX = 999999.99;

// The one label that maps to plain grams; every other unit travels as a pair.
const GRAM_LABEL = "g";

const UNIT_TYPES: readonly MeasurementUnitType[] = [
  "mass",
  "volume",
  "household",
];

// Stand-in for a food whose list never arrived.
export const GRAM_UNIT: MeasurementUnit = Object.freeze({
  id: null,
  is_custom: false,
  type: "mass",
  label: GRAM_LABEL,
  amount: 1,
  grams: 1,
  grams_per_unit: 1,
});

const normalize = (label: string): string => label.trim().toLowerCase();

const round = (value: number, precision: number): number => {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

export const isGramUnit = (unit: MeasurementUnit): boolean =>
  normalize(unit.label) === GRAM_LABEL;

// Matching mirrors the server (trimmed, case-insensitive, first match), so a
// label that resolves here always resolves there.
export const findUnit = (
  units: MeasurementUnit[] | null | undefined,
  label: string | null | undefined
): MeasurementUnit | undefined => {
  if (!Array.isArray(units)) return undefined;
  if (typeof label !== "string" || label.trim().length === 0) return undefined;
  const wanted = normalize(label);
  return units.find((unit) => normalize(unit.label) === wanted);
};

// A list safe to render a picker from: grams are always offered.
export const ensureUnits = (
  units: MeasurementUnit[] | null | undefined
): MeasurementUnit[] => {
  if (!Array.isArray(units) || units.length === 0) return [GRAM_UNIT];
  if (units.some(isGramUnit)) return units;
  return [GRAM_UNIT, ...units];
};

const finiteNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

// Sanitise a raw `measurement_units` array: rows without a label or a usable
// multiplier are dropped, the rest get defaults for the optional fields.
export const parseMeasurementUnits = (raw: unknown): MeasurementUnit[] => {
  if (!Array.isArray(raw)) return [];
  const units: MeasurementUnit[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const item = row as Record<string, unknown>;
    const label = typeof item.label === "string" ? item.label.trim() : "";
    const gramsPerUnit = finiteNumber(item.grams_per_unit);
    if (!label || gramsPerUnit === null || gramsPerUnit <= 0) continue;
    const type = UNIT_TYPES.includes(item.type as MeasurementUnitType)
      ? (item.type as MeasurementUnitType)
      : "household";
    units.push({
      id: finiteNumber(item.id),
      is_custom: item.is_custom === true,
      type,
      label,
      amount: finiteNumber(item.amount) ?? 1,
      grams: finiteNumber(item.grams) ?? gramsPerUnit,
      grams_per_unit: gramsPerUnit,
    });
  }
  return units;
};

// `quantity × grams_per_unit`, rounded the way the server rounds it. Always
// `grams_per_unit`, never `grams`: a USDA "0.5 cup" row (amount 0.5,
// grams 120) weighs 240 g per unit.
export const resolveGrams = (quantity: number, unit: MeasurementUnit): number =>
  round(quantity * unit.grams_per_unit, GRAM_PRECISION);

export const resolveAmount = (
  quantity: number,
  unit: MeasurementUnit
): ComponentAmount => {
  const grams = resolveGrams(quantity, unit);
  return isGramUnit(unit)
    ? { portion_grams: grams, quantity: null, measurement_unit_label: null }
    : { portion_grams: grams, quantity, measurement_unit_label: unit.label };
};

// Grams and the pair never travel together (the API rejects both).
export const toComponentAmount = (
  amount: ComponentAmount
): ComponentAmountPayload =>
  amount.measurement_unit_label === null || amount.quantity === null
    ? { portion_grams: amount.portion_grams }
    : {
        quantity: amount.quantity,
        measurement_unit_label: amount.measurement_unit_label,
      };

export const clampQuantity = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(QUANTITY_MAX, round(value, QUANTITY_MAX_DECIMALS));
};

// The amount field after a unit change: to grams converts ("2 slice" stays
// "50"), to any other unit starts at 1, reselecting the same unit keeps it.
export const amountAfterUnitChange = (
  amount: string,
  from: MeasurementUnit,
  to: MeasurementUnit
): string => {
  if (normalize(from.label) === normalize(to.label)) return amount;
  if (!isGramUnit(to)) return "1";
  const typed = Number(amount);
  if (amount.trim().length === 0 || !Number.isFinite(typed)) return amount;
  return String(round(resolveGrams(typed, from), QUANTITY_MAX_DECIMALS));
};

// A unit label as shown anywhere in the UI. Display only: what travels to
// the API is the list's own spelling.
export const formatUnitLabel = (label: string): string => normalize(label);

// "2 slice" for an amount in a unit, null for one in grams.
export const formatUnitAmount = (
  quantity: number | null | undefined,
  label: string | null | undefined
): string | null => {
  if (typeof quantity !== "number" || !Number.isFinite(quantity)) return null;
  if (typeof label !== "string" || label.trim().length === 0) return null;

  const shown = formatUnitLabel(label);
  const amount = formatNumber(round(quantity, QUANTITY_MAX_DECIMALS));

  // A label may already state its own count ("1 egg", "2 cookies"), so the
  // quantity is dropped at one and multiplied against the measure otherwise.
  if (/^\d/.test(shown)) return quantity === 1 ? shown : `${amount} × ${shown}`;
  return `${amount} ${shown}`;
};

// "2 slice (50 g)", or "50 g" for an amount entered in grams. Grams are
// always shown because they are what the nutrition comes from.
export const formatAmountWithGrams = (
  grams: number,
  quantity: number | null | undefined,
  label: string | null | undefined
): string => {
  const gramsText = `${formatNumber(round(grams, 1))} g`;
  const unitText = formatUnitAmount(quantity, label);
  return unitText ? `${unitText} (${gramsText})` : gramsText;
};
